import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AiVoice from './AiVoice';
import GoogleAiVoice from './google/GoogleAiVoice';
import { googleAiVoiceStyles } from './google/GoogleAiVoiceStyle';

interface IAiVoiceShellProps {
  onSelect?: (aSrc: string, aFileName?: string) => void;
}

type AiVoiceMode = 'google' | 'legacy';

const MODE_STORAGE_KEY = 'ai-voice-shell-mode';

const readInitialMode = (): AiVoiceMode => {
  if (typeof window === 'undefined') return 'legacy';
  return window.localStorage.getItem(MODE_STORAGE_KEY) === 'google' ? 'google' : 'legacy';
};

export default function AiVoiceShell({ onSelect }: IAiVoiceShellProps) {
  const [mode, setMode] = useState<AiVoiceMode>(() => readInitialMode());
  const [googleLoading, setGoogleLoading] = useState(false);
  const theme = useTheme();

  const handleModeChange = (nextMode: AiVoiceMode | null) => {
    if (nextMode === null) return;

    setMode(nextMode);
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, nextMode);
    } catch {
      // no-op
    }
  };

  const isGoogleMode = mode === 'google';
  const modeLabel = isGoogleMode ? 'Switch to Legacy' : 'Click here to beta test our new AI Voice 2.0';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0, backgroundColor: theme.palette.bgTwo.main, position: 'relative' }}>
      {isGoogleMode && googleLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: theme.zIndex.modal + 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: theme.spacing(2) }}>
        <Box>
          {isGoogleMode ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Typography variant='body1' fontWeight='500'>
                AI Voice 2.0
              </Typography>
              <Chip label='Beta' variant='outlined' />
            </div>
          ) : (
            <Typography variant='body1' fontWeight='500'>
              AI Voice Legacy
            </Typography>
          )}
        </Box>

        <Box sx={!isGoogleMode ? googleAiVoiceStyles.modeSwitchOutlineSx(theme) : undefined}>
          <Button
            variant='outlined'
            color='gray'
            size='small'
            onClick={() => handleModeChange(isGoogleMode ? 'legacy' : 'google')}
            style={{ backgroundColor: theme.palette.bgFour.main, color: theme.palette.text.primary }}
            sx={!isGoogleMode ? googleAiVoiceStyles.modeSwitchButtonSx(theme) : undefined}
          >
            {modeLabel}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
        {isGoogleMode ? <GoogleAiVoice onSelect={onSelect} onLoadingChange={setGoogleLoading} /> : <AiVoice onSelect={onSelect} />}
      </Box>
    </Box>
  );
}
